import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { userQueryOptions } from "~/user/data-source/user-query-options";
import { formatUserName } from "~/user/utils/format-user-name";

export function useCurrentUser(options?: { enabled?: boolean }) {
  const auth = useAuth();
  const sub = auth?.user?.profile.sub;

  const usersQuery = useQuery({
    ...userQueryOptions.all(),
    enabled: !!sub && (options?.enabled ?? true),
    select: (users) => users.find((u) => u.providerUserId === sub),
  });

  // `isPending`, not `isLoading`: a disabled query is pending with `fetchStatus: "idle"`, so
  // `isLoading` is false there and the unresolved query would fall through to `"unknown"` — which
  // means "settled, no such user" and is a different answer from "not asked yet".
  if (!sub || usersQuery.isPending) return { status: "loading" } as const;
  if (usersQuery.isError) return { status: "error" } as const;
  if (!usersQuery.data) return { status: "unknown" } as const;
  return {
    status: "ready",
    userId: usersQuery.data.id,
    name: formatUserName(usersQuery.data),
  } as const;
}
